/**
 * Leituras e escritas do retorno da autorização (`TP-056`). O ator NÃO vai no corpo e nenhum instante vai no corpo:
 * o back lê a sessão e usa o relógio dele (a autorização vale da CHEGADA do e-mail, lida do banco).
 */

import { queryOptions } from '@tanstack/react-query';
import type {
  AuthorizationReturnSnapshot,
  CaseReturnsView,
  DescartarRetornoRequest,
  MarcarResultadoRequest,
  PendingReturnsResponse,
} from '@anery/api-contracts';

import { requisitar } from '../api/clienteHttp';
import { mapearRetornoDaMensagem } from '../triagem/mapeadores';
import type { RetornoDaMensagem } from '../triagem/tipos';
import { mapearRetornosAConfirmar, mapearRetornosDoCaso } from './mapeadores';
import type { MotivoDoDescarte, ResultadoDoRetorno, RetornoAConfirmar, RetornosDoCaso } from './tipos';

export const chavesDeRetornos = {
  raiz: ['retornos'] as const,
  lista: (meus: boolean) => ['retornos', 'lista', { meus }] as const,
  doCaso: (casoId: string) => ['retornos', 'caso', casoId] as const,
};

/** `GET /retornos`: os retornos ainda a confirmar do núcleo; `meus` filtra pelos Casos de que a pessoa é dona. */
export function opcoesDosRetornosAConfirmar(meus: boolean) {
  return queryOptions({
    queryKey: chavesDeRetornos.lista(meus),
    queryFn: ({ signal }): Promise<readonly RetornoAConfirmar[]> =>
      requisitar<PendingReturnsResponse>(`/retornos${meus ? '?meus=true' : ''}`, { sinal: signal }).then(
        mapearRetornosAConfirmar,
      ),
  });
}

/** `GET /casos/{id}/retornos`: os retornos do Caso e a vez da Anery. */
export function opcoesDosRetornosDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDeRetornos.doCaso(casoId),
    queryFn: ({ signal }): Promise<RetornosDoCaso> =>
      requisitar<CaseReturnsView>(`/casos/${encodeURIComponent(casoId)}/retornos`, { sinal: signal }).then(
        mapearRetornosDoCaso,
      ),
    enabled: casoId !== '',
  });
}

function caminhoDoRetorno(casoId: string, emailId: string, acao: string): string {
  return `/casos/${encodeURIComponent(casoId)}/retornos/${encodeURIComponent(emailId)}/${acao}`;
}

/** Confirma o que o retorno propõe. Devolve os retornos do Caso já atualizados. */
export function confirmarRetorno(casoId: string, emailId: string): Promise<RetornosDoCaso> {
  return requisitar<CaseReturnsView>(caminhoDoRetorno(casoId, emailId, 'confirmar'), { method: 'POST' }).then(
    mapearRetornosDoCaso,
  );
}

/** Descarta o retorno com um motivo da lista fechada. Nada é aplicado ao Caso. */
export function descartarRetorno(casoId: string, emailId: string, motivo: MotivoDoDescarte): Promise<RetornosDoCaso> {
  const corpo: DescartarRetornoRequest = { reason: motivo };
  return requisitar<CaseReturnsView>(caminhoDoRetorno(casoId, emailId, 'descartar'), { method: 'POST', corpo }).then(
    mapearRetornosDoCaso,
  );
}

/** Fecha a vez da Anery de um questionamento confirmado. */
export function marcarQuestionamentoRespondido(casoId: string, emailId: string): Promise<RetornosDoCaso> {
  return requisitar<CaseReturnsView>(caminhoDoRetorno(casoId, emailId, 'respondido'), { method: 'POST' }).then(
    mapearRetornosDoCaso,
  );
}

/** Marca, na triagem, o resultado do retorno de uma mensagem da thread (olhando a imagem da carta). */
export function marcarResultadoDoRetorno(
  ticketId: string,
  emailId: string,
  resultado: ResultadoDoRetorno,
): Promise<RetornoDaMensagem> {
  const corpo: MarcarResultadoRequest = { result: resultado };
  return requisitar<AuthorizationReturnSnapshot>(
    `/triagem/${encodeURIComponent(ticketId)}/emails/${encodeURIComponent(emailId)}/resultado`,
    { method: 'POST', corpo },
  ).then((snapshot) => {
    const retorno = mapearRetornoDaMensagem(snapshot, 'resultado');
    if (retorno === null) throw new Error('A API marcou o resultado sem devolvê-lo.');
    return retorno;
  });
}
